/**
 * v1 (このアプリ、`/` 配下) ⇔ v2 (新 UI `frontend/`、`/v2` 配下) の UI モード切替。
 *
 * - モードは localStorage (`vega-ui-mode`) に半永続化する。未設定 (初回) は
 *   どちらの URL も直接開ける (リダイレクトしない)
 * - モードが明示的に保存されていて、かつ現在パスがモードと不一致のときだけ
 *   対応するパスへ `location.replace` する (SPA をまたぐためフルナビゲーション)
 * - localStorage が使えない環境 (private mode 等) では機能ごと無効化する
 *
 * NOTE: v2 側の `frontend/src/lib/ui-mode.ts` と同一ロジックのミラー。
 * 片方を変更したらもう片方にも反映すること (過渡期限定の仕掛けなので共有
 * パッケージ化はしない。Epic #237 Phase 3 で v2 が `/` に昇格したら丸ごと削除)。
 * ズレは両側のミラー一致テスト (`uiMode.test.ts` / v2 の `ui-mode.test.ts`) が検出する。
 */

export type UiMode = 'v1' | 'v2';

export const UI_MODE_STORAGE_KEY = 'vega-ui-mode';

export const getStoredUiMode = (): UiMode | null => {
  try {
    const value = window.localStorage.getItem(UI_MODE_STORAGE_KEY);
    return value === 'v1' || value === 'v2' ? value : null;
  } catch {
    return null;
  }
};

export const setStoredUiMode = (mode: UiMode): void => {
  try {
    window.localStorage.setItem(UI_MODE_STORAGE_KEY, mode);
  } catch {
    // localStorage が使えない環境では半永続化を諦める (遷移自体は行う)
  }
};

/** 末尾スラッシュを正規化する (`/v2/` → `/v2`、`/` はそのまま) */
const stripTrailingSlash = (pathname: string): string =>
  pathname.length > 1 && pathname.endsWith('/')
    ? pathname.replace(/\/+$/, '')
    : pathname;

/**
 * v2 のパス (ブラウザ上の `/v2/...`) を対応する v1 のパスへ変換する。
 *
 * - `/v2/catalog/:ns/:kind/:name` → `/catalog/:ns/:kind/:name`
 * - `/v2/docs/:ns/:kind/:name/*` → `/docs/:ns/:kind/:name/*`
 * - `/v2/catalog(?kind=..)` → `/catalog` (v1 の filter クエリへの変換はしない)
 * - `/v2/search` → `/search`、`/v2/docs` → `/docs`
 * - 対応が無いもの (`/v2/` ホーム等) → v1 のトップ `/catalog`
 */
export const toV1Path = (v2Pathname: string): string => {
  const rest = stripTrailingSlash(
    v2Pathname === '/v2' ? '/' : v2Pathname.replace(/^\/v2(?=\/|$)/, ''),
  );
  if (/^\/catalog\/[^/]+\/[^/]+\/[^/]+$/.test(rest)) return rest;
  if (/^\/docs\/[^/]+\/[^/]+\/[^/]+(\/.*)?$/.test(rest)) return rest;
  if (rest === '/catalog' || rest === '/search' || rest === '/docs')
    return rest;
  return '/catalog';
};

/**
 * v1 のパスを対応する v2 のパス (ブラウザ上の `/v2/...`) へ変換する。
 *
 * - `/catalog/:ns/:kind/:name(/*)` → `/v2/catalog/:ns/:kind/:name` (v1 のタブ等の
 *   追加セグメントは v2 に対応が無いため落とす)
 * - `/docs/:ns/:kind/:name/*` → `/v2/docs/:ns/:kind/:name/*`
 * - `/catalog` → `/v2/catalog`、`/search` → `/v2/search`、`/docs` → `/v2/docs`
 * - 対応が無いもの (`/settings` 等) → v2 のトップ `/v2/`
 */
export const toV2Path = (v1Pathname: string): string => {
  const pathname = stripTrailingSlash(v1Pathname);
  const entity = pathname.match(/^\/catalog\/[^/]+\/[^/]+\/[^/]+/);
  if (entity) return `/v2${entity[0]}`;
  if (/^\/docs\/[^/]+\/[^/]+\/[^/]+(\/|$)/.test(pathname))
    return `/v2${pathname}`;
  if (pathname === '/catalog' || pathname === '/search' || pathname === '/docs')
    return `/v2${pathname}`;
  return '/v2/';
};

/**
 * OAuth 認可フロー (MCP クライアントの同意ページ) の着地点かどうか。
 * `/oauth2/` 配下は v1 (`/oauth2/authorize/:sessionId`) も v2
 * (`/v2/oauth2/authorize/:sessionId`) も認可フロー専用で、通常の UI ページは置かない。
 */
const isAuthorizationPath = (pathname: string): boolean =>
  pathname.startsWith('/oauth2/') || pathname.startsWith('/v2/oauth2/');

/**
 * 認可フローの着地点を、`:sessionId` を保ったまま反対側の UI の同じパスへ対応付ける
 * (`/oauth2/authorize/:id` ⇔ `/v2/oauth2/authorize/:id`)。同意ページは v1/v2 どちらにも
 * 同じ route で存在し、同じ auth-backend のセッション API を叩く。
 */
const toAuthorizationPath = (pathname: string, target: UiMode): string => {
  const rest = pathname.replace(/^\/v2(?=\/)/, '');
  return target === 'v1' ? rest : `/v2${rest}`;
};

/**
 * boot 時チェック: 保存されたモードと現在の UI が食い違っていたら対応パスへ
 * `location.replace` する。リダイレクトを開始した場合は true を返す
 * (呼び出し側は描画をスキップして良い)。
 *
 * 各 UI は自分のパス配下でしか起動しないため (`/v2/*` は常に v2、それ以外は常に v1)、
 * リダイレクト先では `mode === currentUi` となり無限ループしない。
 *
 * ただし**認可フローの着地点は UI モードで横取りしない**。着地先は auth-backend の
 * リダイレクト先 (現状は v1 の `/oauth2/authorize/:sessionId`) が決めるものなので、
 * v2 モードの人がここに来ても `/v2/` へ飛ばすと同意画面が出ず、MCP クライアントに
 * 認可コードが返らないまま終わる。
 */
export const redirectIfUiModeMismatch = (currentUi: UiMode): boolean => {
  const { pathname } = window.location;
  if (isAuthorizationPath(pathname)) return false;
  const mode = getStoredUiMode();
  if (mode === null || mode === currentUi) return false;
  window.location.replace(
    mode === 'v1' ? toV1Path(pathname) : toV2Path(pathname),
  );
  return true;
};

/**
 * 切替ボタン用: モードを保存して対応パスへ遷移する。
 *
 * 認可フローの途中だけは `toV1Path` / `toV2Path` を通さず、同じパスの相手側へ移す。
 * 両関数は対応の無いパスをトップへ畳むため `:sessionId` が落ち、同意できないまま MCP
 * クライアントが認可コードを待ち続ける。同意ページは v1 なら Root.tsx の、v2 なら
 * app-sidebar.tsx の切替ボタンが出るサイドバーの中に描画されるので、ここは実際に踏まれる。
 */
export const switchUiMode = (target: UiMode): void => {
  setStoredUiMode(target);
  const { pathname } = window.location;
  if (isAuthorizationPath(pathname)) {
    window.location.replace(toAuthorizationPath(pathname, target));
    return;
  }
  window.location.replace(
    target === 'v1' ? toV1Path(pathname) : toV2Path(pathname),
  );
};
